import { useEffect } from "react";

/**
 * Imperatively patches the document's `<title>` and a small set of
 * Open Graph / share-card `<meta>` tags during a route's lifecycle.
 *
 * Used by the public viewer surfaces (M11 season + game + live)
 * to make shareable match URLs render nicely as link previews in
 * Slack / iMessage / Discord / Facebook / etc. without pulling in
 * a helmet dependency.
 *
 * On mount + when any prop changes:
 *   - Sets `document.title`
 *   - Upserts `<meta name="og:title" content=…>`
 *   - Upserts `<meta property="og:title" content=…>` (Facebook
 *     reads property=, most others read name=)
 *   - Upserts `<meta property="og:description" content=…>`
 *   - Upserts `<meta property="og:image" content=…>` (when provided)
 *   - Upserts `<meta property="og:type" content=…>` (when provided)
 *   - Upserts `<meta name="description" content=…>`
 *
 * On unmount: restores the previously-rendered values so the
 * next-mounted route doesn't inherit stale meta.
 *
 * Why not `react-helmet-async`: one more dep + provider boilerplate
 * for ~30 lines of effect code. The public viewer has at most two
 * meta-driven shells live at a time (season → game navigation) and
 * the per-prop dependency array keeps re-application narrow.
 */

export type DocumentMeta = {
  /** `document.title`. Caller decides whether to append " · Tktic". */
  title?: string;
  description?: string;
  /** OG image URL (absolute or app-relative). Most crawlers want
   * an absolute URL — caller is responsible for prefixing
   * `window.location.origin`. */
  ogImage?: string;
  /** `og:type` — defaults to "website". Use `"video.other"` for
   * match pages, `"video.episode"` for clips, etc. */
  ogType?: string;
};

function upsertMeta(
  selector: string,
  attr: "name" | "property",
  key: string,
  value: string,
): { restore: () => void } {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  const created = el === null;
  const prevContent = el?.content ?? null;
  if (created) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el!.content = value;
  return {
    restore: () => {
      if (created) {
        el?.remove();
      } else if (prevContent !== null) {
        el!.content = prevContent;
      }
    },
  };
}

export function useDocumentMeta(meta: DocumentMeta): void {
  const { title, description, ogImage, ogType } = meta;
  useEffect(() => {
    const restorers: Array<() => void> = [];
    const prevTitle = document.title;
    if (title) document.title = title;
    if (title) {
      restorers.push(
        upsertMeta('meta[name="og:title"]', "name", "og:title", title).restore,
      );
      restorers.push(
        upsertMeta(
          'meta[property="og:title"]',
          "property",
          "og:title",
          title,
        ).restore,
      );
    }
    if (description) {
      restorers.push(
        upsertMeta(
          'meta[name="description"]',
          "name",
          "description",
          description,
        ).restore,
      );
      restorers.push(
        upsertMeta(
          'meta[property="og:description"]',
          "property",
          "og:description",
          description,
        ).restore,
      );
    }
    if (ogImage) {
      restorers.push(
        upsertMeta(
          'meta[property="og:image"]',
          "property",
          "og:image",
          ogImage,
        ).restore,
      );
    }
    if (ogType) {
      restorers.push(
        upsertMeta(
          'meta[property="og:type"]',
          "property",
          "og:type",
          ogType,
        ).restore,
      );
    }
    return () => {
      // Restore in reverse so the most-recently-applied change
      // unwinds first — relevant when the same key was upserted
      // twice (e.g. og:title via both name= and property=).
      for (let i = restorers.length - 1; i >= 0; i -= 1) restorers[i]();
      document.title = prevTitle;
    };
  }, [title, description, ogImage, ogType]);
}
